import { useEffect, useState } from "react"
import { OpenOrder } from "../../types/orders"
import { auth, db } from "../../services/firebase"
import { doc, updateDoc, increment } from "firebase/firestore"
import { toast } from "react-toastify"

import "./style/OpenOrders.scss"

type Props = {
  orders: OpenOrder[]
}

const OpenOrders = ({ orders }: Props) => {
  const [livePrices, setLivePrices] = useState<Record<string, number>>({})

  // Fetch live prices every 5 seconds
  useEffect(() => {
    const symbols = Array.from(new Set(orders.map(o => o.pair.replace("_", ""))))
    const fetchPrices = async () => {
      const results = await Promise.all(
        symbols.map(symbol =>
          fetch(`https://api.binance.com/api/v3/ticker/price?symbol=${symbol}`)
            .then(res => res.json())
            .then(data => [symbol, parseFloat(data.price)] as [string, number])
            .catch(() => [symbol, NaN])
        )
      )
      setLivePrices(Object.fromEntries(results))
    }

    fetchPrices()
    const interval = setInterval(fetchPrices, 5000)
    return () => clearInterval(interval)
  }, [orders])

  // Cancel trade without affecting balance
  const cancelTrade = async (id: string) => {
    const user = auth.currentUser
    if (!user) return

    const tradeRef = doc(db, "users", user.uid, "trades", id)
    await updateDoc(tradeRef, { status: "CANCELED" })
  }

  const closeTrade = async (order: OpenOrder) => {
  const user = auth.currentUser
  if (!user) return

  const symbol = order.pair.replace("_", "")
  const livePrice = livePrices[symbol] ?? order.priceNow
  const delta = order.side === "buy"
    ? livePrice - order.tradePrice
    : order.tradePrice - livePrice
  const pl = delta * order.quantity

  const tradeRef = doc(db, "users", user.uid, "trades", order.id)
  const balanceRef = doc(db, "users", user.uid, "balance", "main")

  try {
    await updateDoc(tradeRef, {
      status: "CLOSED",
      priceNow: livePrice,
      profitLose: pl
    })

    await updateDoc(balanceRef, {
      amount: increment(pl)
    })

    const emoji = pl >= 0 ? "🟢" : "🔴"
    const sign = pl >= 0 ? "+" : "-"
    toast(`${emoji} Closed ${order.pair} trade with ${sign}${Math.abs(pl).toFixed(2)} P/L`)
  } catch (err) {
    console.error("❌ Failed to close trade:", err)
    toast.error("Failed to close trade")
  }
}


  return (
    <div className="open-orders">
      {orders.length === 0 ? (
        <p className="empty">No open orders</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Pair</th>
              <th>Side</th>
              <th>Qty</th>
              <th>Entry</th>
              <th>Live</th>
              <th>P/L</th>
              <th>Time</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o, i) => {
              const symbol = o.pair.replace("_", "")
              const live = livePrices[symbol] ?? o.priceNow
              const delta = o.side === "buy" ? live - o.tradePrice : o.tradePrice - live
              const plNum = delta * o.quantity
              const plStr = `${plNum >= 0 ? "+" : ""}${(20*plNum).toFixed(2)}`
              const plClass = plNum >= 0 ? "profit" : "loss"

              return (
                <tr key={i}>
                  <td>{o.pair}</td>
                  <td className={o.side}>{o.side.toUpperCase()}</td>
                  <td>{o.quantity.toFixed(4)}</td>
                  <td>{o.tradePrice.toFixed(4)}</td>
                  <td>{live.toFixed(4)}</td>
                  <td className={`pl-cell ${plClass}`}>{plStr} $</td>
                  <td>{new Date(o.createdAt).toLocaleTimeString()}</td>
                  <td>
                    <button className="close-btn" onClick={() => closeTrade(o)}>Close</button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}

export default OpenOrders
